/// <amd-module name="Artifacts/Feed/components/PackageListTable" />
import { getPackageListCommands } from "Artifacts/Feed/helpers/PackageCommandsHelper";
import * as Res from "Artifacts/Feed/Resources";
import { getFeedIdentifier } from "Artifacts/Root/helpers/FeedHelpers";
import { encodeName, encodeVersion } from "Artifacts/Root/helpers/PackageDecoderHelper";
import { resizeColumn } from "Artifacts/Root/helpers/TableHelper";
import { IProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { IProtocol } from "Artifacts/Root/protocols/types";
import * as RootRes from "Artifacts/Root/Resources";
import { packageDetailsOverview } from "Artifacts/Root/routing/Constants";
import { IFeed, IPackageVersionIdentity } from "Artifacts/Root/types";
import { PackageListDataItem, ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import * as React from "react";
import { generatePath, Link as RouterLink } from "react-router-dom";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import * as StringUtils from "VSS/Core/Util/String";
import { useComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { IListSelection } from "VSSUI/List";
import { MenuItemType } from "VSSUI/Menu";
import { Pill } from "VSSUI/Pill";
import { PillGroup, PillGroupOverflow } from "VSSUI/PillGroup";
import {
  ColumnMore,
  ColumnSelect,
  ITableBreakpoint,
  ITableColumn,
  renderSimpleCell,
  SimpleTableCell,
  Table,
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { getSafeId } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

interface IPackageListTableItem {
  protocol: IProtocol;
  name: string;
  views: string[];
  source: string;
  lastPushed: Date;
  description: string;
  downloads: number;
  users: number;
  version: string;
  link: string;
  isDeleted: boolean;
  isListed: boolean;
  packageId: string;
  normalizedVersion: string;
}

const tableBreakpoints: ITableBreakpoint[] = [
  {
    breakpoint: ScreenBreakpoints.xsmall,
    columnWidths: [10, 60, -80, -20, 0, 0, 0, 0, 0],
  },
  {
    breakpoint: ScreenBreakpoints.small,
    columnWidths: [10, 60, -50, -25, -25, 0, 0, 0, 0],
  },
  {
    breakpoint: ScreenBreakpoints.large,
    columnWidths: [10, 60, -25, -15, -8, -8, -25, 75, 75],
  },
];

let packageListTableColumns = [
  new ColumnSelect(),
  {
    id: "protocol",
    name: Res.PackageListTable_TableColumn_Protocol,
    readonly: true,
    renderCell: renderProtocol,
    width: new ObservableValue(60),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
  {
    id: "name",
    name: Res.PackageListTable_TableColumn_Name,
    readonly: true,
    renderCell: renderName,
    width: new ObservableValue(-150),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
  {
    id: "views",
    name: Res.PackageListTable_TableColumn_Views,
    readonly: true,
    renderCell: renderViews,
    width: new ObservableValue(-100),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
  {
    id: "source",
    name: Res.PackageListTable_TableColumn_Source,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-50),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
  {
    id: "lastPushed",
    name: Res.PackageListTable_TableColumn_LastPushed,
    readonly: true,
    renderCell: renderLastPushed,
    width: new ObservableValue(-50),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
  {
    id: "description",
    name: Res.PackageListTable_TableColumn_Description,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-150),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
  {
    id: "downloads",
    name: Res.PackageListTable_TableColumn_Downloads,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-50),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
  {
    id: "users",
    name: Res.PackageListTable_TableColumn_Users,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-50),
    onSize: (event, index, width) => {
      packageListTableColumns = resizeColumn(packageListTableColumns, index, width);
    },
  },
] as ITableColumn<IPackageListTableItem | undefined>[];

export interface IPackageListTableProps {
  packageData?: PackageListDataItem[];
  selection: IListSelection;
  feed?: IFeed;
  view?: ViewIdentifier;
}

const tableShimmer = new Array(30).fill(
  new ObservableValue<IPackageListTableItem | undefined>(undefined),
);

const PackageListTable: React.FC<IPackageListTableProps> = ({
  packageData,
  selection,
  feed,
  view,
}) => {
  const { pageContext } = useComponentContext();

  const tableItems = React.useMemo(() => {
    const protocolService: IProtocolService = pageContext.getService<IProtocolService>(
      "IProtocolService",
    );

    if (feed) {
      const items = packageData
        ? packageData.map<IPackageListTableItem>((packageDataItem) => ({
            protocol: protocolService.getProtocolById(packageDataItem.package.protocol),
            name: packageDataItem.package.name,
            packageId: packageDataItem.package.id,
            views: packageDataItem.latestVersion.views.filter((viewName) => viewName !== "Local"),
            source: packageDataItem.latestVersion.directUpstreamSourceId
              ? feed.upstreamSources.find(
                  (source) => source.id === packageDataItem.latestVersion.directUpstreamSourceId,
                )?.name || RootRes.FeedSources_Unknown
              : RootRes.FeedSources_Local,
            lastPushed: packageDataItem.latestVersion.publishDate,
            description: packageDataItem.latestVersion.description,
            downloads: packageDataItem.package.downloadCount,
            users: packageDataItem.package.userCount,
            version: packageDataItem.latestVersion.version,
            normalizedVersion: packageDataItem.latestVersion.normalizedVersion,
            link: generatePath(packageDetailsOverview, {
              feedIdentifier: getFeedIdentifier(feed, view),
              packageName: encodeName(
                packageDataItem.package.name,
                packageDataItem.package.protocol,
              ),
              protocol: packageDataItem.package.protocol,
              version: encodeVersion(
                packageDataItem.latestVersion.version,
                packageDataItem.package.protocol,
              ),
            }),
            isDeleted: packageDataItem.latestVersion.isDeleted,
            isListed: packageDataItem.latestVersion.isListed,
          }))
        : tableShimmer;
      return new ArrayItemProvider<IPackageListTableItem | undefined>(items);
    }
    return new ArrayItemProvider<IPackageListTableItem | undefined>(tableShimmer);
  }, [packageData, pageContext, feed, view]);

  React.useEffect(() => {
    selection.clearUnselectable();
    tableItems?.value.forEach((item, index) => {
      if (item?.isDeleted) {
        selection.addUnselectable(index);
      }
    });
  }, [tableItems, selection]);

  const tableColumns = React.useMemo(() => {
    const protocolService: IProtocolService = pageContext.getService<IProtocolService>(
      "IProtocolService",
    );

    return [
      ...packageListTableColumns,
      new ColumnMore<IPackageListTableItem>(
        // menuProvider
        (item) => ({
          id: "sub-menu",
          items: [
            ...getPackageListCommands(
              pageContext,
              protocolService.getProtocolById(item.protocol.id),
              [
                {
                  name: item.name,
                  packageId: item.packageId,
                  version: item.version,
                  normalizedVersion: item.normalizedVersion,
                  protocol: protocolService.getProtocolById(item.protocol.id).name,
                  views: item.views,
                  isListed: item.isListed,
                  isDeleted: item.isDeleted,
                } as IPackageVersionIdentity,
              ],
              feed,
            ).map((command) => ({
              id: command.id,
              onActivate: command.action,
              text: command.displayName,
              itemType: MenuItemType.Normal,
              iconProps: command.iconProps,
              disabled: item.isDeleted,
            })),
          ],
        }),
        // menuAvailable
        (item) => !item.isDeleted,
        // onActivate
        (rowIndex) => {
          selection.clear();
          selection.select(rowIndex);
        },
      ),
    ];
  }, [pageContext, feed, selection]);

  const ariaMessageId = "package-table-aria-description";
  return (
    <Card
      className="flex-grow bolt-table-card package-list-card"
      contentProps={{ contentPadding: false }}
    >
      <div className="visibility-hidden" id={getSafeId(ariaMessageId)}>
        {RootRes.PackageListSelection_ScreenreaderHelp}
      </div>
      <section
        aria-label={Res.PackageListTable_PackageList}
        aria-describedby={getSafeId(ariaMessageId)}
      >
        <Table
          tableBreakpoints={tableBreakpoints}
          columns={tableColumns}
          itemProvider={tableItems}
          selection={selection}
        />
      </section>
    </Card>
  );
};

function renderLastPushed(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPackageListTableItem>,
  tableItem: IPackageListTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <Ago date={tableItem.lastPushed} />
    </SimpleTableCell>
  );
}

function renderViews(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPackageListTableItem>,
  tableItem: IPackageListTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <Tooltip overflowOnly={true} text={tableItem.views.join(", ")}>
        <PillGroup className="flex-row" overflow={PillGroupOverflow.wrap}>
          {tableItem.views.map((view) => (
            <Pill key={view} excludeFocusZone={true}>
              {view}
            </Pill>
          ))}
        </PillGroup>
      </Tooltip>
    </SimpleTableCell>
  );
}

function renderName(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPackageListTableItem>,
  tableItem: IPackageListTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <div className={"flex-column text-ellipsis"}>
        <Tooltip overflowOnly={true} text={tableItem.name}>
          <RouterLink className="text-ellipsis bolt-link package-version" to={tableItem.link}>
            {tableItem.name}
          </RouterLink>
        </Tooltip>
        <Tooltip
          overflowOnly={true}
          text={StringUtils.format(Res.PackageListTable_TableColumn_Version, tableItem.version)}
        >
          {!tableItem.isDeleted && tableItem.isListed ? (
            <span className="text-ellipsis package-version">
              {StringUtils.format(Res.PackageListTable_TableColumn_Version, tableItem.version)}
            </span>
          ) : (
            <span className="text-ellipsis package-version">
              <del>
                {StringUtils.format(Res.PackageListTable_TableColumn_Version, tableItem.version)}
              </del>
            </span>
          )}
        </Tooltip>
      </div>
    </SimpleTableCell>
  );
}

function renderProtocol(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IPackageListTableItem>,
  tableItem: IPackageListTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <Tooltip text={tableItem.protocol.name}>
        <div>
          <WrappedComponent
            wrappedType={tableItem.protocol.getLogoContributionId()}
            dependencies={[tableItem.protocol.getLogoContributionId()]}
            wrappedProps={{ className: "table-protocol-icon", alt: tableItem.protocol.name }}
          />
        </div>
      </Tooltip>
    </SimpleTableCell>
  );
}

export { PackageListTable };
